import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardSection,
  CardSectionTitle,
  CardTitle,
  Link,
  Text,
} from '@apollo/orbit';
import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useNewFedNextVersionAvailable } from 'src/hooks/useNewFedNextVersionAvailable/useNewFedNextVersionAvailable';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import Config from 'src/lib/config';
import { useIsFedNextEnabled } from 'src/lib/featureFlags';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import { BuildPipelineTrack } from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { ConfigureBuildPipelineModal } from './configureBuildPipelineModal/ConfigureBuildPipelineModal';
import {
  FederationVersionInfo,
  newMinorVersionAvailable,
} from './FederationVersionInfo';

export const useGetDefaultBuildPipelineTrackEnum = () => {
  const allowFedNextAsGraphDefault = useIsFedNextEnabled();

  const getDefaultBuildPipelineTrackEnum = useCallback(
    (
      defaultBuildPipelineTrack: string | null | undefined,
      tagEnabled: boolean,
    ): GraphQLTypes.BuildPipelineTrack | undefined => {
      switch (defaultBuildPipelineTrack) {
        case 'FED_NEXT':
          // Note: we don't support FED_NEXT as a default BPT since it is unstable.
          if (allowFedNextAsGraphDefault) {
            return BuildPipelineTrack.FED_NEXT;
          } else {
            return assertUnreachableOrReturnDefault(
              defaultBuildPipelineTrack as never,
              undefined,
            );
          }
        case '2.9':
          return BuildPipelineTrack.FED_2_9;
        case '2.8':
          return BuildPipelineTrack.FED_2_8;
        case '2.7':
          return BuildPipelineTrack.FED_2_7;
        case '2.6':
          return BuildPipelineTrack.FED_2_6;
        case '2.5':
          return BuildPipelineTrack.FED_2_5;
        case '2.4':
          return BuildPipelineTrack.FED_2_4;
        case '2.3':
          return BuildPipelineTrack.FED_2_3;
        case '2.1':
          return BuildPipelineTrack.FED_2_1;
        case '2':
          return BuildPipelineTrack.FED_2_0;
        case '1': {
          if (tagEnabled) return BuildPipelineTrack.FED_1_1;
          return BuildPipelineTrack.FED_1_0;
        }
        case undefined:
        case null:
          return undefined;
        default:
          return assertUnreachableOrReturnDefault(
            defaultBuildPipelineTrack as never,
            undefined,
          );
      }
    },
    [allowFedNextAsGraphDefault],
  );

  return { getDefaultBuildPipelineTrackEnum };
};

export const BuildConfigCard = ({
  isGraph = false,
  serviceId,
  variant,
  canManageBuildConfig,
  graphType,
  className,
  defaultBuildPipelineTrack,
  defaultBuildPipelineTrackUpdatedAt,
  refetchData,
  routerVersion,
}: {
  isGraph: boolean;
  serviceId: string;
  variant:
    | GraphQLTypes.VariantGeneralSettingsPageQuery_graph_variant
    | GraphQLTypes.Graph__GraphSettingsQuery_service_variant;
  canManageBuildConfig: boolean;
  graphType: GraphQLTypes.GraphType;
  defaultBuildPipelineTrack?: string | null;
  defaultBuildPipelineTrackUpdatedAt?: Timestamp['output'] | null;
  className?: string;
  refetchData: () => void;
  routerVersion?: string;
}) => {
  const location = useLocation();
  const newFedNextVersionAvailable = useNewFedNextVersionAvailable();
  const { getDefaultBuildPipelineTrackEnum } =
    useGetDefaultBuildPipelineTrackEnum();

  const heading = isGraph ? 'Default build pipeline' : 'Build pipeline';

  const description = isGraph
    ? 'Configure the default build settings for your graph. The version specified here will apply to any newly created variants.'
    : 'Configure build settings for this variant.';

  const version =
    isGraph && defaultBuildPipelineTrack
      ? defaultBuildPipelineTrack
      : variant.compositionVersion;
  const latestLaunch = variant.latestLaunch ?? null;
  const latestLaunchCompositionVersion =
    (latestLaunch?.buildInput.__typename === 'CompositionBuildInput' &&
      latestLaunch.buildInput.version) ||
    '';
  const versionUpdatedAt = isGraph
    ? defaultBuildPipelineTrackUpdatedAt
    : variant.buildConfigUpdatedAt;
  const tagEnabled = !!variant.supportedDirectives?.find(
    (directive) => directive.name === 'tag' && directive.enabled,
  );

  const defaultBuildPipelineTrackEnum = getDefaultBuildPipelineTrackEnum(
    defaultBuildPipelineTrack,
    tagEnabled,
  );

  const allowedTracksNonNull = useMemo(
    () => variant.allowedTracks.filter((t): t is NonNullable<typeof t> => !!t),
    [variant],
  );
  const isUsingFedNext =
    variant.compositionVersion === BuildPipelineTrack.FED_NEXT;
  const newVersionAvailable = isUsingFedNext
    ? newFedNextVersionAvailable({ latestLaunch })
    : newMinorVersionAvailable(allowedTracksNonNull, version as string);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{heading}</CardTitle>
        <CardDescription>{description}</CardDescription>
        {variant.hasManagedSubgraphs ? (
          <Tooltip
            disabled={canManageBuildConfig}
            label="You do not have permission to edit the build config"
          >
            {newVersionAvailable ? (
              <Button
                isDisabled={!canManageBuildConfig}
                variant="primary"
                as={Link}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.configureBuildPipeline,
                })}
              >
                Upgrade
              </Button>
            ) : (
              <Button
                isDisabled={!canManageBuildConfig}
                variant="secondary"
                as={Link}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.configureBuildPipeline,
                })}
              >
                Configure
              </Button>
            )}
          </Tooltip>
        ) : null}
      </CardHeader>
      <CardBody>
        <CardSection>
          <CardSectionTitle>
            Federation version
            <Tooltip
              placement="top"
              label={`The version of Apollo Federation to use when building this ${
                isGraph ? 'graph' : 'variant'
              }.`}
            >
              <IconInfo className="text-secondary" />
            </Tooltip>
          </CardSectionTitle>
          {!variant.hasManagedSubgraphs || !variant.buildConfig ? (
            <div className="flex flex-row gap-1">
              <Text className="italic">
                This {`${isGraph ? 'graph' : 'variant'}`} is not federated.
              </Text>
              <Link
                to="https://www.apollographql.com/docs/federation/managed-federation/setup"
                isExternal
              >
                Learn more about how to federate your graph
              </Link>
            </div>
          ) : version ? (
            <FederationVersionInfo
              compositionVersion={version}
              allowedTracks={allowedTracksNonNull}
              latestLaunchCompositionVersion={latestLaunchCompositionVersion}
              versionUpdatedAt={versionUpdatedAt}
              buildPipelineTrack={
                isGraph && defaultBuildPipelineTrackEnum
                  ? defaultBuildPipelineTrackEnum
                  : variant.buildConfig.buildPipelineTrack
              }
              isUsingFedNext={isUsingFedNext}
            />
          ) : (
            'No version set.'
          )}
        </CardSection>
        <LazyUrlModal name={Config.modals.configureBuildPipeline}>
          {({ closeModal, isOpen }) => (
            <ConfigureBuildPipelineModal
              isOpen={isOpen}
              isGraph={isGraph}
              serviceId={serviceId}
              variant={variant}
              onClose={closeModal}
              refetchGraphData={refetchData}
              graphType={graphType}
              allowedTracks={allowedTracksNonNull}
              defaultBuildPipelineTrack={defaultBuildPipelineTrackEnum}
              routerVersion={routerVersion}
              canUpdateFedNextVersion={
                isUsingFedNext ? newVersionAvailable : false
              }
            />
          )}
        </LazyUrlModal>
      </CardBody>
    </Card>
  );
};
